import { typographyTokens } from '../../theme/typographyTokens';
import React, { Fragment, useMemo, useState } from 'react';
import { styled } from '@mui/material/styles';
import {
  Box,
  TableCell,
  TableRow,
  Typography,
  Tooltip,
  TooltipProps,
  tooltipClasses,
  Button,
  Badge,
  Card,
  CardContent,
  Chip,
  Divider,
  useTheme
} from '@mui/material';
import { useAccount } from '@azure/msal-react';
import SepNotesDrawer from '../SepNotesDrawer';
import { SEPSearchRow, SEPPhase, SEPStatus } from '../../../shared/types/SEP';
import { TaskExtended, TaskStatus, defaultSystemTask } from '../../../shared/types/Task';
import OnTheClockIcon from '../../assets/svg/OnTheClockIcon';
import CurrentPhaseIcon from '../../assets/svg/CurrentPhaseIcon';
import DepartmentIconRenderer from '../../assets/svg/departmentIconMap';
import { newUiColorTokens } from '../../theme/themeTokens';
import { Link } from 'react-router-dom';
import UserDisplay from '../UserDisplay/UserDisplay';
import { getActiveTaskCountsByDepartment, getTaskProgress, getStatus, HeadCell } from './SepTableBody';
import { useGetSepTasksQuery } from '../../services/sepSlice';
import { useGetTaskTemplatesSLAQuery } from '../../services/tasksSlice/tasksSlice';
import RequestProgressDial from '../RequestProgressDial/RequestProgressDial';
import { GetDataFieldOptionTemplate } from '../../utils/dataFieldOptionTemplate'
import { DataField, DataFieldOption, DataFieldsEnum, DataFieldsOptionsEnum } from '../../../shared/types/DataField';
import SepAge from './SepAge';
import { format, parseISO } from 'date-fns';
import { WorkflowIconBox } from '../../utils/workflowIconConfig';

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#646669',
    color: '#FBFCFC',
    boxShadow: theme.shadows[1],
    fontSize: typographyTokens.fontSizes.body,
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  '&': {
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.default : '#fff',
    // separator is rendered as a Divider row
    border: 'none',
  },
}));

const StyledLink = styled(Link)(({ theme }) => ({
  textDecoration: 'none',
  color: theme.palette.mode === 'dark' ? theme.palette.white.main : theme.palette.solidBlue.main,
  '&:hover': {
    color: theme.palette.mode === 'dark' ? theme.palette.solidBlue.main : theme.palette.gray.main,
  },
}));

const formatTime = (dateStr: string) => {
  const year = dateStr.slice(0, 4);
  const month = dateStr.slice(5, 7);
  const day = dateStr.slice(8, 10);
  return `${month}/${day}/${year}`;
};

const SepPhaseDetail = ({ sep }: { sep: SEPSearchRow }) => {
  const { data: tasks, isLoading: isTasksLoading } = useGetSepTasksQuery(sep.id);
  const { data: taskTemplatesSLA } = useGetTaskTemplatesSLAQuery();
  const theme = useTheme();
  const account = useAccount();
  const isDarkMode = theme.palette.mode === 'dark';
  const taskOrderByTemplateId = useMemo(
    () => new Map((taskTemplatesSLA || []).map((template) => [template.id, template.taskOrderId])),
    [taskTemplatesSLA]
  );

  const phaseNames: Record<string, string> = {
    validation: 'Validation',
    initiate: 'Initiate',
    design: 'Design',
    implement: 'Implement',
  };

  const renderPhaseCard = (phase: string) => {
    const phaseTasks = ((tasks || []) as TaskExtended[])
      .filter((task) => task.enabled && String(task.phase) === phase)
      .sort((firstTask, secondTask) => {
        const firstDepartmentName = firstTask.department?.name || '';
        const secondDepartmentName = secondTask.department?.name || '';
        const firstDepartmentOrder = firstTask.department?.departmentDisplayOrderId;
        const secondDepartmentOrder = secondTask.department?.departmentDisplayOrderId;
        const hasFirstDepartmentOrder = firstDepartmentOrder !== null && firstDepartmentOrder !== undefined;
        const hasSecondDepartmentOrder = secondDepartmentOrder !== null && secondDepartmentOrder !== undefined;

        if (firstTask.departmentID !== secondTask.departmentID) {
          if (hasFirstDepartmentOrder && hasSecondDepartmentOrder && firstDepartmentOrder !== secondDepartmentOrder) {
            return firstDepartmentOrder - secondDepartmentOrder;
          }
          if (hasFirstDepartmentOrder !== hasSecondDepartmentOrder) {
            return hasFirstDepartmentOrder ? -1 : 1;
          }
          return firstDepartmentName.localeCompare(secondDepartmentName);
        }

        const hasFirstTemplate = firstTask.taskTemplateID !== null && firstTask.taskTemplateID !== undefined;
        const hasSecondTemplate = secondTask.taskTemplateID !== null && secondTask.taskTemplateID !== undefined;

        if (hasFirstTemplate !== hasSecondTemplate) {
          return hasFirstTemplate ? 1 : -1;
        }
        if (!hasFirstTemplate) {
          return firstTask.name.localeCompare(secondTask.name);
        }

        const firstTaskOrder = taskOrderByTemplateId.get(firstTask.taskTemplateID!);
        const secondTaskOrder = taskOrderByTemplateId.get(secondTask.taskTemplateID!);
        const hasFirstTaskOrder = firstTaskOrder !== null && firstTaskOrder !== undefined;
        const hasSecondTaskOrder = secondTaskOrder !== null && secondTaskOrder !== undefined;

        if (hasFirstTaskOrder && hasSecondTaskOrder && firstTaskOrder !== secondTaskOrder) {
          return firstTaskOrder - secondTaskOrder;
        }
        if (hasFirstTaskOrder !== hasSecondTaskOrder) {
          return hasFirstTaskOrder ? -1 : 1;
        }
        return firstTask.name.localeCompare(secondTask.name);
      });
    const groupedTasks = phaseTasks.reduce<Record<string, TaskExtended[]>>((groups, task) => {
      const departmentName = task.department?.name || `Department ${task.departmentID}`;
      if (!groups[departmentName]) groups[departmentName] = [];
      groups[departmentName].push(task);
      return groups;
    }, {});
    const departments = Object.entries(groupedTasks);
    const myActiveTasksCount = phaseTasks.filter(
      (task) =>
        account?.localAccountId === task.assignee?.id &&
        (task.status === TaskStatus.todo ||
          task.status === TaskStatus.inReview ||
          task.status === TaskStatus.changesRequested)
    ).length;

    return (
      <Card
        key={phase}
        variant="outlined"
        sx={{
          width: '100%',
          minWidth: 0,
          height: '100%',
          borderColor: 'divider',
          borderRadius: '2px',
          boxShadow: theme.shadows[3],
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: 'text.primary',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          {String(sep.phase) === phase && (
            <CurrentPhaseIcon circleColor={newUiColorTokens.prime.blue} />
          )}
          <Typography sx={{ color: isDarkMode ? theme.palette.content.white : theme.palette.content.default, flexShrink: 0 }} variant="h6">{phaseNames[phase]} Phase</Typography>
          {myActiveTasksCount > 0 && (
            <Chip
              label={`You're on the clock for ${myActiveTasksCount} ${myActiveTasksCount === 1 ? 'task' : 'tasks'}`}
              size="small"
              icon={<OnTheClockIcon color={theme.palette.content.white} />}
              sx={{
                ml: 'auto',
                maxWidth: '100%',
                fontWeight: typographyTokens.fontWeights.medium,
                fontSize: typographyTokens.fontSizes.caption,
                color: theme.palette.content.white,
                backgroundColor: theme.palette.prime.orange,
              }}
            />
          )}
        </Box>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          {isTasksLoading ? (
            <Box
              sx={{
                minHeight: '160px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: isDarkMode ? theme.palette.content.white : theme.palette.content.default,
                  fontWeight: typographyTokens.fontWeights.medium,
                }}
              >
                Loading task details...
              </Typography>
            </Box>
          ) : departments.length === 0 ? (
            <Box
              sx={{
                minHeight: '160px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
              }}
            >
              <Typography color="text.secondary">No tasks</Typography>
            </Box>
          ) : (
            departments.map(([departmentName, departmentTasks]) => (
              <Box key={departmentName} sx={{ '&:not(:first-of-type)': { mt: 2 } }}>
                <Chip
                  size="small"
                  label={departmentName}
                  icon={
                    <DepartmentIconRenderer
                      departmentName={departmentName}
                      height={16}
                      width={16}
                      color={isDarkMode ? theme.palette.content.white : theme.palette.content.default}
                    />
                  }
                  sx={{
                    mb: 0.75,
                    px: 0.5,
                    maxWidth: '100%',
                    fontWeight: typographyTokens.fontWeights.semibold,
                    fontSize: typographyTokens.fontSizes.caption,
                    color: isDarkMode ? theme.palette.content.white : theme.palette.content.default,
                  }}
                />
                {departmentTasks.map((task) => {
                  const assignee = task.assignee?.displayName
                    || [task.assignee?.givenName, task.assignee?.surname].filter(Boolean).join(' ')
                    || task.assignedUserID
                    || 'Unassigned';
                  const isSystemTask = task.defaultAssignee === defaultSystemTask.systemTask;
                  const isAssignedToCurrentUser = account?.localAccountId === task.assignee?.id;
                  const isLocked = task.status === TaskStatus.pending;
                  const isOnTheClock = task.status === TaskStatus.todo
                    || task.status === TaskStatus.inReview
                    || task.status === TaskStatus.changesRequested;

                  return (
                    <Box
                      key={task.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        py: 1.25,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <StyledLink to={`/sep/${sep.id}/task/${task.id}`}>
                        <Typography component="span" variant="body2" sx={{ lineHeight: 1.75 }}>
                          {task.shortName || task.name}
                        </Typography>
                      </StyledLink>
                      {isSystemTask ? (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ flexShrink: 0, textAlign: 'right', lineHeight: 1.75 }}
                        >
                          {defaultSystemTask.systemTrackingTask}
                        </Typography>
                      ) : isAssignedToCurrentUser ? (
                        <Chip
                          label="You"
                          size="small"
                          icon={isOnTheClock ? <OnTheClockIcon color={theme.palette.content.white} /> : undefined}
                          sx={{
                            flexShrink: 0,
                            fontWeight: typographyTokens.fontWeights.medium,
                            fontSize: typographyTokens.fontSizes.caption,
                            color: theme.palette.content.white,
                            backgroundColor: isLocked
                              ? theme.palette.prime.granite
                              : isOnTheClock
                                ? theme.palette.prime.orange
                                : theme.palette.prime.green,
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            flexShrink: 0,
                            textAlign: 'right',
                            color: isDarkMode ? theme.palette.content.white : theme.palette.content.default,
                          }}
                        >
                          {isOnTheClock && (
                            <OnTheClockIcon color={theme.palette.prime.blue} style={{ marginRight: '4px' }} />
                          )}
                          <Typography variant="body2" sx={{ lineHeight: 1.75 }}>{assignee}</Typography>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            ))
          )}
        </CardContent>
      </Card>
    );
  };

  const hasImplementTasks = (tasks || []).some(
    (task: TaskExtended) => task.enabled && String(task.phase) === 'implement'
  );

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      {/* <Box sx={{ pb: 6 }}>
        {tasks && <SepContacts sep={sep} tasks={tasks} />}
      </Box> */}
      <Box sx={{ width: '100%', minWidth: 0, p: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 2, backgroundColor: isDarkMode ? theme.palette.background.default : theme.palette.supportingAdditional.tealUltraLight }}>
        {['validation', 'initiate', 'design'].map(renderPhaseCard)}
        {hasImplementTasks && (
          <Box sx={{ width: '100%', minWidth: 0, gridColumn: { xs: 'auto', md: '1' } }}>
            {renderPhaseCard('implement')}
          </Box>
        )}
      </Box>
    </Box>
  );
};

const withComponent = (str: string, phase: string) => {
  if (
    phase === 'pending' ||
    phase === 'waiting' ||
    (typeof window !== 'undefined' && window.location.href.replaceAll('/', '').slice(-8) === 'all-seps')
  ) {
    return (
      <Box
        sx={{
          border: '2px solid #FF7400',
          borderRadius: '20px',
          px: '12px',
          py: '6px',
          width: 'fit-content',
          position: 'relative',
        }}
      >
        {str}
      </Box>
    );
  } else if (phase === 'todo') {
    return (
      <Box
        sx={{
          border: '2px solid #FF7400',
          borderRadius: '18px',
          px: '12px',
          py: '6px',
          width: 'fit-content',
          position: 'relative',
          paddingLeft: '40px',
        }}
      >
        <Box
          sx={{
            backgroundColor: '#FF7400',
            width: '35px',
            height: '35px',
            borderRadius: '18px',
            position: 'absolute',
            top: '-2px',
            left: '-1px',
            textAlign: 'center',
            paddingTop: '10px',
          }}
        >
          <i className="fa-solid fa-flag" style={{ color: 'white' }}></i>
        </Box>
        {str}
      </Box>
    );
  } else {
    <Box
      sx={{
        border: '2px solid #FF7400',
        borderRadius: '20px',
        px: '12px',
        py: '6px',
        width: 'fit-content',
        position: 'relative',
      }}
    >
      {str}
    </Box>;
  }
};

const SepPhaseDetails = ({
  row,
  onDetailShow,
  detailShow,
  headers,
  screenName,
  onSubscriptionChange,
  currentUserEmail,
  onStatusAction,
}: {
  row: SEPSearchRow;
  onDetailShow: (isShow: boolean) => void;
  detailShow: boolean;
  headers: HeadCell[];
  screenName?: string;
  onSubscriptionChange?: (row: SEPSearchRow) => void;
  currentUserEmail?: string;
  onStatusAction?: (row: SEPSearchRow, status: 'rejected' | 'cancelled') => void;
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const account = useAccount();
  const [notesOpen, setNotesOpen] = useState<boolean>(false);
  const sx = detailShow
    ? {
      position: 'sticky',
      top: 76,
      zIndex: 800,
      // cells set their own background, so target them to tint the expanded row
      '& .MuiTableCell-root': {
        backgroundColor: isDarkMode ? theme.palette.background.default : theme.palette.supportingAdditional.tealUltraLight,
      },
    }
    : {};
  function handlePhaseClick(): void {
    throw new Error('Function not implemented.');
  }

  const workflowData: any = GetDataFieldOptionTemplate(DataFieldsEnum.WORKFLOW_TYPE, row?.workFlowType);
  const designReview: any = GetDataFieldOptionTemplate(DataFieldsEnum.WORKFLOW_TYPE, DataFieldsOptionsEnum.SEP_DESIGN_REVIEW);
  const workflowLabel = row?.workFlowType || 'SEP Design Review';
  const activeTaskCountsByAssignee = getActiveTaskCountsByDepartment(
    row,
    account?.localAccountId
  );
  const phaseLabel = [SEPStatus.cancelled, SEPStatus.expired, SEPStatus.rejected].includes(row.status)
    ? 'Complete'
    : row.phase === SEPPhase.knockout
      ? 'Draft'
      : row.phase.slice(0, 1).toUpperCase() + row.phase.slice(1);

  const getDataFieldValue = (fieldName: string, dataFields: DataField[], dataFieldOptions: DataFieldOption[]) => {
    const field = dataFields.find((df: DataField) => df.name === fieldName);
    if (field && field.value) {
      return field.value;
    } else {
      const option = dataFieldOptions.filter(
        (option: DataFieldOption) => option.dataFieldID === field?.id
      );
      const selectedOptions =
        option
          ?.filter((op: DataFieldOption) => op.selected)
          ?.map((opt: DataFieldOption) => opt?.value) || [];
      return (selectedOptions?.length > 0 && selectedOptions.join(", ")) || "";
    }
  };

  return (
    <>
      <SepNotesDrawer
        open={notesOpen}
        onClose={() => setNotesOpen(false)}
        row={row}
      />
      <TableRow
        hover
        aria-label={`SEPs Row ${row.name}`}
        tabIndex={-1}
        key={row.id}
        sx={sx}
      >
        <StyledTableCell
          variant="head"
          sx={{
            left: 0,
            maxWidth: '44px',
            minWidth: '44px',
            zIndex: 800,
            cursor: 'pointer',
            //...sx,
          }}
        >
          <i
            className={`fa-sharp fa-solid fa-chevron-${detailShow ? 'up' : 'down'
              }`}
            onClick={() => onDetailShow(!detailShow)}
          ></i>
        </StyledTableCell>
        {headers
          .filter((e) => e.visible)
          .map((header) => (
            <Fragment key={header.id}>
              {header.id === 'id' && (
                <StyledTableCell
                  aria-label="SEP Id"
                  variant="head"
                  sx={{
                    left: 44,
                    maxWidth: '400px',
                    minWidth: '400px',
                    zIndex: 800,
                    //...sx,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 1.5, width: '100%' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '23%', flexShrink: 0 }}>
                      <LightTooltip title={workflowLabel}>
                        <Box component="span">
                          <WorkflowIconBox
                            workflowType={workflowLabel}
                            phase={row.phase as SEPPhase}
                            colorScheme="primeBlue"
                          />
                        </Box>
                      </LightTooltip>
                      <Typography
                        variant="caption"
                        sx={{ lineHeight: 1.2, mt: 0.5, textAlign: 'center' }}
                      >
                        {phaseLabel}
                      </Typography>
                    </Box>
                    <LightTooltip title={row.name}>
                      <StyledLink to={`/sep/${row.id}`}>
                        <Typography
                          component="span"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: '2',
                            WebkitBoxOrient: 'vertical',
                            fontSize: typographyTokens.fontSizes.body,
                          }}
                        >
                          {row.id}{row.name ? ` - ${row.name}` : ''}
                        </Typography>
                      </StyledLink>
                    </LightTooltip>
                  </Box>
                </StyledTableCell>
              )}
              {header.id === 'taskProgress' && (
                <StyledTableCell
                  sx={{
                    maxWidth: '800px',
                    minWidth: '600px',
                    zIndex: 500
                  }}
                  aria-label="Tasks Progress"
                  variant="head"
                >
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', width: '100%' }}>
                    {[
                      {
                        key: 'current-user',
                        counts: activeTaskCountsByAssignee.assignedToCurrentUser,
                        badgeColor: theme.palette.prime.orange,
                      },
                      {
                        key: 'other-users',
                        counts: activeTaskCountsByAssignee.assignedToOtherUsers,
                        badgeColor: theme.palette.prime.granite,
                      },
                    ].map(({ key, counts, badgeColor }) => (
                      <Box
                        key={key}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: 0,
                          px: 1,
                          borderRight: key === 'current-user' ? '1px solid' : 0,
                          borderColor: key === 'current-user' ? 'divider' : undefined,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, flexWrap: 'wrap', minWidth: 0 }}>
                          {Object.entries(counts).map(([departmentName, count]) => (
                            <LightTooltip
                              key={departmentName}
                              title={`${departmentName}: ${count} active ${count === 1 ? 'task' : 'tasks'}`}
                            >
                              <Badge
                                badgeContent={count}
                                sx={{
                                  '& > .MuiBadge-badge': {
                                    backgroundColor: badgeColor,
                                    color: theme.palette.content.white,
                                  },
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 33,
                                    height: 33,
                                    border: '1px solid',
                                    borderColor: theme.palette.prime.blue,
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <DepartmentIconRenderer
                                    departmentName={departmentName}
                                    height={22}
                                    width={22}
                                    color={theme.palette.prime.blue}
                                  />
                                </Box>
                              </Badge>
                            </LightTooltip>
                          ))}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </StyledTableCell>
              )}
              {header.id === 'requestor' && (
                <StyledTableCell
                  sx={{ minWidth: '220px', zIndex: 500 }}
                  aria-label="Assigned"
                  variant="head"
                >
                  <UserDisplay
                    showPicture
                    showName
                    userID={row.creator.id}
                    user={row.creator}
                  />
                </StyledTableCell>
              )}
              {header.id === 'notes' && (
                <StyledTableCell
                  sx={{ minWidth: '120px', zIndex: 500 }}
                  aria-label="Notes"
                  variant="head"
                >
                  <Button
                    variant="text"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setNotesOpen(true);
                    }}
                    sx={{
                      textTransform: 'none',
                      fontWeight: theme.typography.fontWeightRegular,
                      fontSize: theme.typography.caption.fontSize,
                      color: theme.palette.prime.blue,
                      p: 0,
                      minWidth: 'auto',
                      '&:hover': {
                        textDecoration: 'underline',
                        backgroundColor: 'transparent',
                      },
                    }}
                  >
                    {row.userNotesCount > 0 ? `My Notes (${row.userNotesCount})` : 'Add Note'}
                  </Button>
                </StyledTableCell>
              )}
              {header.id === 'created_at' && (
                <StyledTableCell
                  sx={{ minWidth: '160px', zIndex: 500 }}
                  aria-label="Requested_date"
                  variant="head"
                >
                  {format(parseISO((row?.startedAt ?? row?.createdAt) as string), 'P')}
                </StyledTableCell>
              )}
              {header.id === 'sepAge' && (
                <StyledTableCell
                  sx={{ minWidth: '120px', zIndex: 500 }}
                  aria-label="SEP Age"
                  variant="head"
                >
                  <SepAge sepObject={row} showPhaseText={false} />
                </StyledTableCell>
              )}
              {header.id === 'apmID' && (
                <StyledTableCell
                  sx={{ minWidth: '120px', zIndex: 500 }}
                  aria-label="APM ID"
                  variant="head"
                >
                  {row.apmID}
                </StyledTableCell>
              )}
              {header.id === 'departmentCode' && (
                <StyledTableCell
                  sx={{ minWidth: '120px', zIndex: 500 }}
                  aria-label="Department Code"
                  variant="head"
                >
                  {!isNaN(Number(row.creator.department?.split(' ')[0])) ? row.creator.department?.split(' ')[0] : 'NA'}
                </StyledTableCell>
              )}
              {header.id === 'validatedDate' && (
                <StyledTableCell
                  sx={{ minWidth: '120px', zIndex: 500 }}
                  aria-label="Validated Date"
                  variant="head"
                >
                  {row.validationDate ? formatTime(row.validationDate) : ''}
                </StyledTableCell>
              )}
              {header.id === 'closedDate' && (
                <StyledTableCell
                  sx={{ minWidth: '120px', zIndex: 500 }}
                  aria-label="Closed Date"
                  variant="head"
                >
                  {row.completedAt ? formatTime(row.completedAt.toString()) : ''}
                </StyledTableCell>
              )}
              {header.id === 'lastUpdated' && (
                <StyledTableCell
                  sx={{ minWidth: '120px', zIndex: 500 }}
                  aria-label="Last Updated"
                  variant="head"
                >
                  {formatTime(row.updatedAt)}
                </StyledTableCell>
              )}
              {header.id === 'requestArea' && row.dataFields && row.dataFieldOptions && (
                <StyledTableCell
                  sx={{ minWidth: '120px', zIndex: 500 }}
                  aria-label="Request Area"
                  variant="head"
                >
                  {getDataFieldValue('Request Area', row.dataFields, row.dataFieldOptions)}
                </StyledTableCell>
              )}
              {header.id === 'isNewOrExistingApplication' && row.dataFields && row.dataFieldOptions && (
                <StyledTableCell
                  sx={{ minWidth: '120px', zIndex: 500 }}
                  aria-label="Is New or Existing Application"
                  variant="head"
                >
                  {getDataFieldValue('Is this an existing application or a new application?', row.dataFields, row.dataFieldOptions)}
                </StyledTableCell>
              )}
              {header.id === 'keyTopics' && row.dataFields && row.dataFieldOptions && (
                <StyledTableCell
                  sx={{ minWidth: '120px', zIndex: 500 }}
                  aria-label="Key Topics"
                  variant="head"
                >
                  {getDataFieldValue('Key Topics', row.dataFields, row.dataFieldOptions)}
                </StyledTableCell>
              )}
              {header.id === 'businessUnit' && row.dataFields && row.dataFieldOptions && (
                <StyledTableCell
                  sx={{ minWidth: '160px', zIndex: 500 }}
                  aria-label="Business Unit"
                  variant="head"
                >
                  {getDataFieldValue('Business Unit', row.dataFields, row.dataFieldOptions)}
                </StyledTableCell>
              )}
              {header.id === 'status' && (
                <StyledTableCell
                  sx={{
                    minWidth: '130px',
                    zIndex: 500,
                    textTransform: 'capitalize',
                  }}
                  aria-label="SEP Status"
                  variant="head"
                >
                  {/* {row.status === 'active' ? 'In Progress' : row.status} */}
                  <Chip
                    variant="outlined"
                    label={row?.status === SEPStatus.active ? 'In Progress' : row?.status}
                    sx={{
                      fontWeight: typographyTokens.fontWeights.semibold,
                      fontSize: typographyTokens.fontSizes.sm,
                      height: 28,
                      borderRadius: '14px',
                      textTransform: 'capitalize',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderColor: row?.phase === SEPPhase.complete ? theme.palette.prime.green : theme.palette.prime.blue,
                      color: row?.phase === SEPPhase.complete ? theme.palette.prime.green : theme.palette.prime.blue,
                      '& .MuiChip-label': { p: 0 },
                      '& .MuiChip-icon': { m: 0 },
                    }}
                  />
                </StyledTableCell>
              )}
              {header.id === 'phase' && (
                <StyledTableCell
                  sx={{ minWidth: '160px', zIndex: 500 }}
                  aria-label="Tasks DependentTaskCount"
                  variant="head"
                >
                  {[
                    SEPStatus.cancelled,
                    SEPStatus.expired,
                    SEPStatus.rejected,
                  ].some((status) => status === row.status)
                    ? 'Complete'
                    : row.phase === 'knockout' ? 'Draft'
                      : row.phase.slice(0, 1).toUpperCase() + row.phase.slice(1)}
                </StyledTableCell>
              )}
              {header.id === 'workFlowType' && (
                <StyledTableCell
                  sx={{ minWidth: '160px', zIndex: 500 }}
                  aria-label="Tasks DependentTaskCount"
                  variant="head"
                >
                  {row?.workFlowType ? row?.workFlowType : "SEP Design Review"}
                </StyledTableCell>
              )}
              {header.id === 'requestProgress' && (((designReview?.sepProgressBar && row?.workFlowType === null) || workflowData?.sepProgressBar) && ![
                SEPStatus.cancelled,
                SEPStatus.rejected,
                SEPStatus.expired,
              ].includes(row.status as SEPStatus) ? (
                <StyledTableCell
                  sx={{ minWidth: '130px', maxWidth: '130px', zIndex: 500, padding: '8px 4px' }}
                  aria-label="Phase Progress"
                  variant="head"
                >
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                    <RequestProgressDial row={row} />
                  </Box>
                </StyledTableCell>
              ) : (
                <StyledTableCell
                  sx={{ minWidth: '130px', maxWidth: '130px', zIndex: 500, textAlign: 'center' }}
                  aria-label="Phase Progress NA"
                  variant="head"
                >
                  NA
                </StyledTableCell>
              ))}
              {header.id === 'subscription' && (
                <StyledTableCell
                  sx={{ minWidth: '140px', zIndex: 500 }}
                  aria-label="Subscription"
                  variant="head"
                >
                  <Button
                    variant="text"
                    size="small"
                    onClick={(event) => {
                      event.stopPropagation();
                      onSubscriptionChange?.(row);
                    }}
                    sx={{
                      textTransform: 'none',
                      fontSize: typographyTokens.fontSizes.caption,
                      color: theme.palette.prime.blue,
                      p: 0,
                      minWidth: 'auto',
                    }}
                  >
                    {(row.subscribers || '').split(',').map((email) => email.trim()).filter(Boolean).includes(currentUserEmail || '')
                      ? 'Unsubscribe'
                      : 'Subscribe'}
                  </Button>
                </StyledTableCell>
              )}
              {header.id === 'reject' && (
                <StyledTableCell
                  sx={{ minWidth: '100px', zIndex: 500 }}
                  aria-label="Reject SEP"
                  variant="head"
                >
                  {row.status === SEPStatus.cancelled ? null : row.status === SEPStatus.rejected ? (
                    'Rejected'
                  ) : (
                    <Button
                      variant="text"
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        onStatusAction?.(row, 'rejected');
                      }}
                      sx={{ textTransform: 'none', minWidth: 'auto', p: 0 }}
                    >
                      Reject
                    </Button>
                  )}
                </StyledTableCell>
              )}
              {header.id === 'cancel' && (
                <StyledTableCell
                  sx={{ minWidth: '100px', zIndex: 500 }}
                  aria-label="Cancel SEP"
                  variant="head"
                >
                  {row.status === SEPStatus.rejected ? null : row.status === SEPStatus.cancelled ? (
                    'Cancelled'
                  ) : (
                    <Button
                      variant="text"
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        onStatusAction?.(row, 'cancelled');
                      }}
                      sx={{ textTransform: 'none', minWidth: 'auto', p: 0 }}
                    >
                      Cancel
                    </Button>
                  )}
                </StyledTableCell>
              )}
            </Fragment>
          ))}
      </TableRow>
      {detailShow && (
        <TableRow>
          <StyledTableCell colSpan={headers.filter((header) => header.visible).length + 1} sx={{ p: 0 }}>
            <SepPhaseDetail sep={row} />
          </StyledTableCell>
        </TableRow>
      )}
      <TableRow>
        <TableCell
          colSpan={headers.filter((header) => header.visible).length + 1}
          sx={{ p: 0, border: 0 }}
        >
          <Divider sx={{ borderColor: theme.palette.elements.divider }} />
        </TableCell>
      </TableRow>
    </>
  );
};

export default SepPhaseDetails;